import { Component, OnInit, OnDestroy, NgZone, ElementRef, ViewChild, ChangeDetectorRef, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, switchMap, timer, exhaustMap, filter, take } from 'rxjs';
import { IconComponent, ButtonComponent, CheckboxComponent } from '@kin/spark-ui';
import { AuthService } from '../auth/auth.service';
import { AddressService } from '../property/address.service';
import { PropertyAssessmentService } from '../property/property-assessment.service';
import { SigningService, AuthorizationDuration } from '../signing/signing.service';
import { UserProfileService } from '../user-profile.service';
import { CaseService } from '../case.service';
import { County, COUNTY_COPY } from '../county/county-config';
import { StepperComponent } from '../stepper/stepper.component';
import { PropertySidebarComponent } from '../property-sidebar/property-sidebar.component';
import { environment } from '../../environments/environment';
import HelloSign from 'hellosign-embedded';

/** Possible states for the signing workflow */
type SignState = 'loading' | 'signing' | 'complete' | 'error';

@Component({
  selector: 'app-sign',
  standalone: true,
  imports: [IconComponent, ButtonComponent, CheckboxComponent, StepperComponent, PropertySidebarComponent],
  templateUrl: './sign.component.html',
  styleUrl: './sign.component.css',
})
export class SignComponent implements OnInit, OnDestroy {
  @ViewChild('signingContainer', { static: false }) signingContainerRef!: ElementRef<HTMLDivElement>;

  private router = inject(Router);
  private auth = inject(AuthService);
  private ngZone = inject(NgZone);
  private signingService = inject(SigningService);
  protected addressService = inject(AddressService);
  protected assessment = inject(PropertyAssessmentService);
  protected userProfile = inject(UserProfileService);
  private caseService = inject(CaseService);
  private cdr = inject(ChangeDetectorRef);

  private hsClient: HelloSign | null = null;

  private static readonly DOC_URL_KEY = 'kin_document_url';

  protected get county(): County {
    return this.assessment.data?.property?.countyCode ?? 'harris';
  }
  filingNoun = COUNTY_COPY[this.county].filingNoun;
  pageTitle = COUNTY_COPY[this.county].pageTitle;
  authorized = false;
  authorizationDuration: AuthorizationDuration = 'ongoing';
  state: SignState = 'loading';
  documentUrl: string | null = null;
  // Local-fallback default so the template always has a sensible string before the first
  // response resolves; overwritten with the resolved (backend-or-fallback) value in
  // loadSignatureRequest once SigningService responds.
  documentName: string = COUNTY_COPY[this.county].documentName;
  errorMessage = '';
  private signatureRequestId: string | null = null;

  private pollSub: Subscription | null = null;

  ngOnInit(): void {
    // Warms CaseService's cache (no-op if confirm's ngOnInit already fetched it) so
    // PropertySidebarComponent's real filingDeadline/complianceCopy are available here too,
    // even on a direct navigation to /sign.
    this.caseService.getCase().subscribe();

    if (this.signingService.isSigningComplete) {
      this.state = 'complete';
      this.documentUrl = sessionStorage.getItem(SignComponent.DOC_URL_KEY);
      this.signatureRequestId = this.signingService.signingResponse?.signatureRequestId ?? null;
      // If we already have the doc URL cached, just refresh in background.
      // Otherwise, poll for it (document may still be processing).
      if (this.documentUrl) {
        this.refreshDocumentUrl();
      } else {
        this.pollForSignedThenDocUrl();
      }
    } else {
      this.loadSignatureRequest();
    }
    this.caseService.getAuthorization().subscribe({
      next: (res) => { this.authorized = res.authorized; },
      error: (err) => {
        if (err.status === 404) {
          this.caseService.createCase({ conditions: [] }).subscribe({
            next: () => {
              this.caseService.getAuthorization().subscribe({
                next: (res) => { this.authorized = res.authorized; },
              });
            },
          });
        }
      },
    });
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
    if (this.hsClient) {
      this.hsClient.close();
      this.hsClient = null;
    }
  }

  back(): void {
    this.router.navigate(['/confirm']);
  }

  get ownerName(): string {
    return this.userProfile.profile?.owner.name ?? '';
  }

  get canContinue(): boolean {
    return this.state === 'complete';
  }

  continueToCase(): void {
    this.caseService.saveAuthorization().subscribe();

    this.auth.updateProgress('your-case').subscribe({
      next: () => this.router.navigate(['/your-case']),
      error: () => this.router.navigate(['/your-case']),
    });
  }

  retry(): void {
    this.signingService.fullReset();
    sessionStorage.removeItem(SignComponent.DOC_URL_KEY);
    this.pollSub?.unsubscribe();
    if (this.hsClient) {
      this.hsClient.close();
      this.hsClient = null;
    }
    this.state = 'loading';
    this.errorMessage = '';
    this.loadSignatureRequest();
  }

  onDurationChange(duration: AuthorizationDuration): void {
    if (this.state === 'complete' || this.signingService.isSigningComplete) return;
    this.authorizationDuration = duration;
    this.signingService.fullReset();
    if (this.hsClient) {
      this.hsClient.close();
      this.hsClient = null;
    }
    this.state = 'loading';
    this.loadSignatureRequest();
  }

  private loadSignatureRequest(): void {
    this.signingService.createSignatureRequest(this.authorizationDuration, this.county).subscribe({
      next: (res) => {
        this.signatureRequestId = res.signatureRequestId;
        this.documentName = res.documentName;

        if (res.status === 'signed') {
          this.state = 'complete';
          this.signingService.markSigningComplete();
          if (res.documentUrl) {
            this.documentUrl = res.documentUrl;
            sessionStorage.setItem(SignComponent.DOC_URL_KEY, res.documentUrl);
          } else {
            // Signed but PDF still processing — poll for it
            this.pollForSignedThenDocUrl();
          }
          return;
        }
        if (res.status === 'declined') {
          this.state = 'error';
          this.errorMessage = 'You previously declined the signing. You can try again or go back.';
          return;
        }
        if (res.status === 'error' || !res.signingUrl) {
          this.state = 'error';
          this.errorMessage = 'Unable to load the signing document. Please try again.';
          return;
        }

        // status === 'awaiting_signature' — embed the signing UI
        this.state = 'signing';

        // Force change detection so the @if block renders the container div,
        // then open HelloSign in the next microtask
        this.cdr.detectChanges();
        setTimeout(() => this.openHelloSign(res.signingUrl!, res.clientId));
      },
      error: () => {
        this.state = 'error';
        this.errorMessage = 'Unable to load the signing document. Please try again.';
      },
    });
  }

  private openHelloSign(signingUrl: string, clientId: string): void {
    const container = this.signingContainerRef?.nativeElement;
    if (!container) return;

    this.hsClient = new HelloSign({ clientId });

    this.hsClient.on('sign', () => {
      this.ngZone.run(() => this.handleSigningComplete());
    });

    this.hsClient.on('decline', () => {
      this.ngZone.run(() => {
        this.state = 'error';
        this.errorMessage = 'You declined the signing. You can try again or go back.';
      });
    });

    this.hsClient.on('error', () => {
      this.ngZone.run(() => {
        this.state = 'error';
        this.errorMessage = 'Something went wrong during signing. Please try again.';
      });
    });

    this.hsClient.on('cancel', () => {
      this.ngZone.run(() => {
        // User closed without signing — treat as navigating back
        this.router.navigate(['/confirm']);
      });
    });

    this.hsClient.on('finish', () => {
      this.ngZone.run(() => this.handleSigningComplete());
    });

    this.hsClient.open(signingUrl, {
      container,
      skipDomainVerification: environment.skipDomainVerification ?? false,
    });
  }

  private handleSigningComplete(): void {
    if (this.state === 'complete') return;
    this.state = 'complete';
    this.signingService.markSigningComplete();
    if (this.hsClient) {
      this.hsClient.close();
      this.hsClient = null;
    }
    this.auth.updateProgress('your-case').subscribe();
    this.pollForSignedThenDocUrl();
  }

  /** Single background POST to refresh the cached documentUrl. */
  private refreshDocumentUrl(): void {
    this.signingService.clear();
    this.signingService.createSignatureRequest(undefined, this.county).subscribe({
      next: (res) => {
        if (res.documentUrl && res.documentUrl !== this.documentUrl) {
          this.documentUrl = res.documentUrl;
          sessionStorage.setItem(SignComponent.DOC_URL_KEY, res.documentUrl);
          this.cdr.detectChanges();
        }
      },
    });
  }

  /**
   * Polls GET /signing/request/:id/status until "signed",
   * then POSTs /signing/request to get documentUrl.
   * If documentUrl is still null, keeps polling.
   */
  private pollForSignedThenDocUrl(): void {
    if (!this.signatureRequestId) {
      // No ID cached — do a POST first to get the signatureRequestId
      this.signingService.clear();
      this.signingService.createSignatureRequest(undefined, this.county).subscribe({
        next: (res) => {
          this.signatureRequestId = res.signatureRequestId;
          if (res.documentUrl) {
            this.documentUrl = res.documentUrl;
            sessionStorage.setItem(SignComponent.DOC_URL_KEY, res.documentUrl);
            this.cdr.detectChanges();
          } else {
            this.startStatusPolling(res.signatureRequestId);
          }
        },
      });
      return;
    }
    this.startStatusPolling(this.signatureRequestId);
  }

  /** Poll GET /status until "signed", then POST for documentUrl. */
  private startStatusPolling(requestId: string): void {
    this.pollSub = timer(3_000, 5_000)
      .pipe(
        take(36), // ~3 minutes
        exhaustMap(() => this.signingService.getSignatureRequestStatus(requestId)),
        filter((status) => status.status === 'signed'),
        take(1),
        // Status is "signed" — now POST to get documentUrl
        switchMap(() => {
          this.signingService.clear();
          return this.signingService.createSignatureRequest(undefined, this.county);
        }),
      )
      .subscribe({
        next: (res) => {
          if (res.documentUrl) {
            this.documentUrl = res.documentUrl;
            sessionStorage.setItem(SignComponent.DOC_URL_KEY, res.documentUrl);
            this.cdr.detectChanges();
          } else {
            // Signed but PDF still generating — poll again for the documentUrl
            this.pollForDocumentUrl();
          }
        },
      });
  }

  /** After status is "signed", poll POST /signing/request until documentUrl is present. */
  private pollForDocumentUrl(): void {
    this.pollSub = timer(3_000, 5_000)
      .pipe(
        take(24), // ~2 minutes
        exhaustMap(() => {
          this.signingService.clear();
          return this.signingService.createSignatureRequest(undefined, this.county);
        }),
        filter((res) => !!res.documentUrl),
        take(1),
      )
      .subscribe({
        next: (res) => {
          this.documentUrl = res.documentUrl!;
          sessionStorage.setItem(SignComponent.DOC_URL_KEY, res.documentUrl!);
          this.cdr.detectChanges();
        },
      });
  }
}
